import React, { useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import Accordion from 'devextreme-react/accordion';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';

const StableItemRenderContext = React.createContext(null);

function StableItemContent({ child }) {
    const { itemRender, session } = React.useContext(StableItemRenderContext);
    if (itemRender) return itemRender(child?.name ?? child);
    return React.cloneElement(child, { session });
}

export default forwardRef(function (props, ref) {
    const accordionRef = useRef();
    useImperativeHandle(ref, () => accordionRef.current);

    const itemRender = useCallback(
        (child) => {
            if (props.itemRender) {
                const name = child?.name ?? child;
                return props.itemRender(name);
            }
            return React.cloneElement(child, {
                session: props.session
            });
        },
        [props.itemRender, props.session]
    );

    // Keep the DevExtreme template itself stable while its React content receives
    // the latest voucher data through context.
    const stableItemRender = useCallback((child) => <StableItemContent child={child} />, []);
    const renderContext = useMemo(() => ({ itemRender: props.itemRender, session: props.session }), [props.itemRender, props.session]);

    const itemTitleRender = useCallback(
        (child, index) => {
            let title = child?.props?.title ?? child?.caption ?? child?.label ?? child?.name ?? child;
            title = AntLocalization.translateText(AntCondition.getValue({}, title));

            if (props.showIndex) title = `${parseInt(index) + 1}. ${title}`;

            const className = 'ant-accordion-title';
            if (props.isH6) return <h6 className={className}>{title}</h6>;
            if (props.isH5) return <h5 className={className}>{title}</h5>;
            if (props.isH4) return <h4 className={className}>{title}</h4>;
            return <h2 className={className}>{title}</h2>;
        },
        [props.showIndex, props.isH4, props.isH5, props.isH6]
    );

    const initDataSource = useCallback(() => {
        if (Array.isArray(props.dataSource)) return props.dataSource;
        if (Array.isArray(props.children)) {
            return props.children.filter((item) => {
                if (item?.props) return AntCondition.isVisible(null, item.props);
                return Boolean(item);
            });
        }
        if (props.children) {
            const item = props.children;
            if (item?.props) {
                if (AntCondition.isVisible(null, item.props)) return [item];
            } else if (item) {
                return [item];
            }
        }
        return [];
    }, [props.dataSource, props.children]);

    const currentDataSource = useMemo(() => initDataSource(), [initDataSource]);
    // Named tabs are the only safe case: the cached item is used solely as an
    // identity, while StableItemContent obtains fresh content from itemRender.
    const canStabilizeItems =
        props.stableItems && currentDataSource.every((child) => child && typeof child === 'object' && child.name != null);
    const stableItemsKey = canStabilizeItems
        ? JSON.stringify(currentDataSource.map((child) => {
              const title = child?.props?.title ?? child?.caption ?? child?.label ?? child?.name ?? child;
              return [child.name, AntLocalization.translateText(AntCondition.getValue({}, title))];
          }))
        : null;
    const stableDataSource = useMemo(() => currentDataSource, [stableItemsKey]);
    const dataSource = canStabilizeItems ? stableDataSource : currentDataSource;

    const onContentReady = useCallback(
        (e) => {
            if (props.autoExpandAll || dataSource.length === 1) {
                dataSource.forEach((_, i) => e.component.expandItem(i));
            } else {
                e.component.expandItem(props.selectedIndex ?? 0);
            }
        },
        [props.autoExpandAll, props.selectedIndex, dataSource]
    );

    return (
        <StableItemRenderContext.Provider value={renderContext}>
            <Accordion
                ref={accordionRef}
                elementAttr={{
                    class: !props.autoHideTitle || (Array.isArray(props.dataSource) && props.dataSource.length > 1)
                        ? 'ant-accordion-multiple'
                        : 'ant-accordion-single'
                }}
                itemRender={canStabilizeItems ? stableItemRender : itemRender}
                itemTitleRender={itemTitleRender}
                dataSource={dataSource}
                noDataText=""
                collapsible={dataSource.length <= 1 ? false : (props.collapsible ?? true)}
                multiple={props.autoExpandAll || (props.multiple ?? false)}
                animationDuration={props.animationDuration ?? 270}
                deferRendering={props.deferRendering}
                onSelectionChanged={props.onSelectionChanged}
                onContentReady={onContentReady}
            />
        </StableItemRenderContext.Provider>
    );
});
